// src/components/ModalTauxHoraireClasse.jsx
// ============================================================================
// DEMANDER UN NOUVEAU TAUX HORAIRE POUR UNE CLASSE
// ============================================================================
// Décisions de Daniel du 01/11/2026 : le taux horaire est celui de la classe,
// jamais celui de l'enseignant (la fiche suit sa classe), et il ne change que
// par une demande validée au Centre de Support, avec un mois d'effet.
//
// La fenêtre lit le contexte au serveur (taux en vigueur, mois d'effet
// possibles, enseignants payés à l'heure concernés, demande en attente) et
// n'en recalcule rien. Elle envoie : nouveau taux, mois d'effet, motif.
// Le serveur refait tous les contrôles, à l'envoi puis à l'approbation.
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Clock, Loader2, Send } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from './ui/dialog';
import LoadingSpinner from './LoadingSpinner';
import { demanderTauxClasse, lireContexteTauxClasse, lireHistoriqueTauxClasse } from './tauxHoraireClasse';

const BOUTON_SECONDAIRE = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';
const BOUTON_PRINCIPAL = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-md bg-[#246990] px-4 text-sm font-medium text-white transition hover:bg-[#1f5a7a] disabled:cursor-not-allowed disabled:opacity-50';
const CHAMP = 'h-9 w-full rounded-lg border border-gray-200 px-3 text-sm text-gray-900 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15 disabled:bg-gray-100 disabled:text-gray-500';

const MOTIF_MIN = 10;
const MOTIF_MAX = 500;
const NOMS_MONTRES = 12;

const formaterTaux = (montant) => `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(montant || 0)))} FCFA`;

const LIBELLES_STATUT = {
  en_attente: { texte: 'En attente', classe: 'border-amber-200 bg-amber-50 text-amber-800' },
  approuvee: { texte: 'Approuvée', classe: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  refusee: { texte: 'Refusée', classe: 'border-red-200 bg-red-50 text-red-700' },
  annulee: { texte: 'Annulée', classe: 'border-gray-200 bg-gray-50 text-gray-600' }
};

const ModalTauxHoraireClasse = ({ classe, open, onClose, onEnvoyee }) => {
  const [contexte, setContexte] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [erreurLecture, setErreurLecture] = useState(null);
  const [nouveauTaux, setNouveauTaux] = useState('');
  const [moisEffetId, setMoisEffetId] = useState('');
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreurEnvoi, setErreurEnvoi] = useState(null);

  const classeId = classe?.id;

  useEffect(() => {
    if (!open || !classeId) return undefined;
    let actif = true;
    setChargement(true);
    setErreurLecture(null);
    setErreurEnvoi(null);
    setNouveauTaux('');
    setMotif('');
    Promise.all([lireContexteTauxClasse(classeId), lireHistoriqueTauxClasse(classeId).catch(() => [])])
      .then(([lu, lignes]) => {
        if (!actif) return;
        setContexte(lu);
        setHistorique(lignes);
        setMoisEffetId(lu?.mois_effet_propose ? String(lu.mois_effet_propose.id) : '');
      })
      .catch((e) => {
        if (actif) setErreurLecture(e.message || 'Le contexte du taux horaire n\'a pas pu être lu.');
      })
      .finally(() => {
        if (actif) setChargement(false);
      });
    return () => { actif = false; };
  }, [open, classeId]);

  const eligibles = contexte?.mois_eligibles || [];
  const moisFermes = useMemo(
    () => (contexte?.mois || []).filter((m) => !m.eligible && m.raison !== 'pas_commence'),
    [contexte]
  );
  const moisChoisi = eligibles.find((m) => String(m.id) === String(moisEffetId)) || null;
  const demandeEnAttente = contexte?.demande_en_attente || null;
  const tauxActuel = Number(contexte?.taux_actuel || 0);
  const tauxSaisi = nouveauTaux.trim() === '' ? null : Number(nouveauTaux);
  const tauxValide = tauxSaisi !== null && Number.isInteger(tauxSaisi) && tauxSaisi > 0 && tauxSaisi !== tauxActuel;
  const longueurMotif = motif.trim().length;
  const motifValide = longueurMotif >= MOTIF_MIN && longueurMotif <= MOTIF_MAX;
  const formulaireFerme = !contexte || Boolean(demandeEnAttente) || eligibles.length === 0;
  const peutEnvoyer = !formulaireFerme && tauxValide && motifValide && moisChoisi && !envoi;

  const enseignants = contexte?.enseignants || [];
  const nomsMontres = enseignants.slice(0, NOMS_MONTRES).map((e) => e.nom);
  const autres = enseignants.length - nomsMontres.length;

  const aideTaux = () => {
    if (tauxSaisi === null) return 'En FCFA de l\'heure, nombre entier.';
    if (!Number.isInteger(tauxSaisi) || tauxSaisi <= 0) return 'Un nombre entier de FCFA, supérieur à 0.';
    if (tauxSaisi === tauxActuel) return 'C\'est déjà le taux en vigueur.';
    return null;
  };

  const envoyer = async () => {
    if (!peutEnvoyer) return;
    setEnvoi(true);
    setErreurEnvoi(null);
    try {
      const resultat = await demanderTauxClasse(classeId, {
        nouveauTaux: tauxSaisi,
        moisEffetId: Number(moisEffetId),
        motif: motif.trim()
      });
      onEnvoyee?.(
        `Demande n° ${resultat.demande_id} envoyée au Centre de Support : ${formaterTaux(tauxSaisi)} de l'heure `
        + `pour ${contexte.classe.nom} à partir de ${moisChoisi.nom}, après validation.`
      );
      onClose();
    } catch (e) {
      setErreurEnvoi(e.message || 'La demande n\'a pas pu être envoyée.');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(ouvert) => { if (!ouvert && !envoi) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
            <Clock className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Taux horaire {contexte?.classe?.nom ? `- ${contexte.classe.nom}` : classe?.nom ? `- ${classe.nom}` : ''}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Le nouveau taux s'applique après validation au Centre de Support, à partir du mois d'effet choisi.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {chargement && !contexte && <LoadingSpinner message="Lecture du taux de la classe..." />}

          {erreurLecture && (
            <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{erreurLecture}</p>
          )}

          {contexte && (
            <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <div className="space-y-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Taux en vigueur</p>
                  <p className="mt-1 text-base font-semibold text-gray-900">
                    {tauxActuel > 0 ? `${formaterTaux(tauxActuel)} de l'heure` : 'Non défini : les heures sont payées 0 FCFA'}
                  </p>
                </div>

                {demandeEnAttente && (
                  <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                    <p>
                      Demande n° {demandeEnAttente.id} envoyée le {demandeEnAttente.envoyee_le}
                      {demandeEnAttente.nouveau_taux > 0 ? ` : ${formaterTaux(demandeEnAttente.nouveau_taux)} de l'heure` : ''}
                      {demandeEnAttente.mois_effet_nom ? ` à partir de ${demandeEnAttente.mois_effet_nom}` : ''}.
                      {' '}Elle doit être traitée au Centre de Support avant d'en envoyer une autre.
                    </p>
                  </div>
                )}

                {!demandeEnAttente && eligibles.length === 0 && (
                  <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    Aucun mois d'effet n'est possible : tous les mois de l'année sont figés, payés ou pas encore commencés.
                  </p>
                )}

                <div>
                  <label htmlFor="taux-nouveau" className="mb-1 block text-sm text-gray-700">Nouveau taux</label>
                  <div className="relative">
                    <input
                      id="taux-nouveau"
                      type="number"
                      inputMode="numeric"
                      min="1"
                      step="1"
                      value={nouveauTaux}
                      onChange={(e) => setNouveauTaux(e.target.value)}
                      onWheel={(e) => e.currentTarget.blur()}
                      disabled={formulaireFerme || envoi}
                      className={`${CHAMP} pr-24`}
                      placeholder="Ex. 1500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">FCFA / heure</span>
                  </div>
                  {aideTaux() && <p className="mt-1 text-[11px] text-gray-500">{aideTaux()}</p>}
                </div>

                <div>
                  <label htmlFor="taux-mois" className="mb-1 block text-sm text-gray-700">Mois d'effet</label>
                  <select
                    id="taux-mois"
                    value={moisEffetId}
                    onChange={(e) => setMoisEffetId(e.target.value)}
                    disabled={formulaireFerme || envoi}
                    className={CHAMP}
                  >
                    {eligibles.length === 0 && <option value="">Aucun mois possible</option>}
                    {eligibles.map((m) => (
                      <option key={m.id} value={m.id}>{m.nom}</option>
                    ))}
                  </select>
                  {moisFermes.length > 0 && (
                    <p className="mt-1 text-[11px] text-gray-500">
                      Fermés : {moisFermes.map((m) => `${m.nom} (${m.raison_texte})`).join(' ; ')}.
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="taux-motif" className="mb-1 block text-sm text-gray-700">Motif (obligatoire)</label>
                  <textarea
                    id="taux-motif"
                    rows={3}
                    maxLength={MOTIF_MAX}
                    value={motif}
                    onChange={(e) => setMotif(e.target.value)}
                    disabled={formulaireFerme || envoi}
                    className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15 disabled:bg-gray-100"
                    placeholder="Pourquoi ce taux : décision de la direction, grille de l'année..."
                  />
                  <p className="mt-1 text-[11px] text-gray-500">
                    {longueurMotif < MOTIF_MIN
                      ? `Encore ${MOTIF_MIN - longueurMotif} caractère${MOTIF_MIN - longueurMotif > 1 ? 's' : ''} au moins.`
                      : `${longueurMotif} / ${MOTIF_MAX} caractères.`}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border border-gray-200 p-4">
                  <p className="text-sm font-semibold text-gray-900">
                    {enseignants.length > 0
                      ? `${enseignants.length} enseignant${enseignants.length > 1 ? 's' : ''} payé${enseignants.length > 1 ? 's' : ''} à l'heure dans cette classe`
                      : "Aucun enseignant payé à l'heure dans cette classe aujourd'hui"}
                  </p>
                  {nomsMontres.length > 0 && (
                    <p className="mt-1 text-sm text-gray-600">
                      {nomsMontres.join(', ')}{autres > 0 ? ` et ${autres} autre${autres > 1 ? 's' : ''}` : ''}.
                    </p>
                  )}
                  <p className="mt-2 text-[11px] text-gray-500">
                    Leur paie suit le taux de la classe : rien n'est à rattraper dans leurs fiches.
                  </p>
                </div>

                <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">
                  <p className="font-semibold">Après validation</p>
                  <p className="mt-1">
                    {moisChoisi && tauxValide
                      ? `Les heures à partir de ${moisChoisi.nom} seront payées ${formaterTaux(tauxSaisi)} de l'heure ; `
                        + `les mois d'avant, non figés, gardent ${formaterTaux(tauxActuel)}.`
                      : "Les heures à partir du mois d'effet seront payées au nouveau taux ; les mois d'avant, non figés, gardent le taux en vigueur."}
                  </p>
                </div>

                {historique.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Demandes précédentes</p>
                    <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200">
                      {historique.slice(0, 5).map((h) => {
                        const statut = LIBELLES_STATUT[h.statut] || LIBELLES_STATUT.annulee;
                        return (
                          <li key={h.id} className="flex items-start justify-between gap-3 px-3 py-2">
                            <div className="min-w-0 text-sm">
                              <p className="text-gray-900">
                                {formaterTaux(h.ancien_taux)} - {formaterTaux(h.nouveau_taux)}
                                {h.mois_effet_nom ? `, à partir de ${h.mois_effet_nom}` : ''}
                              </p>
                              <p className="text-[11px] text-gray-500">
                                Demandée le {h.demandee_le || '-'}{h.demandeur_nom ? ` par ${h.demandeur_nom}` : ''}
                                {h.decidee_le ? ` ; décidée le ${h.decidee_le}` : ''}
                              </p>
                            </div>
                            <span className={`inline-flex h-6 flex-shrink-0 items-center rounded-md border px-2 text-[10px] font-semibold ${statut.classe}`}>
                              {statut.texte}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {erreurEnvoi && (
            <p className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{erreurEnvoi}</p>
          )}
        </div>

        <DialogFooter className="gap-2 border-t bg-gray-50 px-5 py-4 sm:space-x-0">
          <button type="button" onClick={onClose} disabled={envoi} className={BOUTON_SECONDAIRE}>
            Annuler
          </button>
          <button type="button" onClick={envoyer} disabled={!peutEnvoyer} className={BOUTON_PRINCIPAL}>
            {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Envoyer la demande
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalTauxHoraireClasse;
